import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status", "progress", "progressBar"]
  static values = {
    proposalId: String,
    companySlug: String,
    bundleId: String,
    pollingInterval: { type: Number, default: 3000 }
  }

  connect() {
    // Start polling if proposal is generating
    if (this.shouldPoll()) {
      this.startPolling()
    }
  }

  disconnect() {
    this.stopPolling()
  }

  shouldPoll() {
    const status = this.element.dataset.proposalStatus
    return status === "generating"
  }

  startPolling() {
    if (this.pollingTimer) {
      return // Already polling
    }

    this.pollingTimer = setInterval(() => {
      this.checkStatus()
    }, this.pollingIntervalValue)
  }

  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = null
    }
  }

  async checkStatus() {
    try {
      const url = `/companies/${this.companySlugValue}/prospecting_bundles/${this.bundleIdValue}/prospect_proposals/${this.proposalIdValue}/status`

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.updateStatus(data)

      // Stop polling if not generating (draft, review, approved, or any other status)
      // This ensures we stop even if there's an error state
      if (data.status !== "generating") {
        this.stopPolling()
        
        // Reload page after a short delay to show final results or error
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      }
    } catch (error) {
      console.error("Error checking proposal status:", error)
      // Don't stop polling on error, just log it
    }
  }

  updateStatus(data) {
    // Update status badge
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.humanize(data.status)
      this.statusTarget.className = this.getStatusClass(data.status)
    }

    // Update progress
    if (this.hasProgressTarget) {
      this.progressTarget.textContent = `${data.progress || 0}%`
    }

    // Update progress bar
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${data.progress || 0}%`
    }

    // Update element dataset for shouldPoll check
    this.element.dataset.proposalStatus = data.status
  }

  getStatusClass(status) {
    const baseClasses = "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
    const statusClasses = {
      "draft": "bg-gray-100 text-gray-800",
      "generating": "bg-blue-100 text-blue-800",
      "review": "bg-yellow-100 text-yellow-800",
      "approved": "bg-green-100 text-green-800",
      "rejected": "bg-red-100 text-red-800"
    }
    return `${baseClasses} ${statusClasses[status] || "bg-gray-100 text-gray-800"}`
  }

  humanize(str) {
    if (!str) return ""
    return str
      .replace(/_/g, " ")
      .replace(/\b\w/g, l => l.toUpperCase())
  }
}
